import React, { useState, useRef, useEffect } from 'react';
import IA from "@images/IA.svg";
import { aiAssistantService } from '@/api/aiAssistant';
import { isCompanyUser } from '@/utils/roleStorage';

const AI = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    
    // You'll need to get this from your auth context or props
    const isCompany = isCompanyUser()
    
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Business theme classes
    const businessClasses = {
        gradientPrimary: 'bg-tale-business',
        gradientLight: 'bg-tale-business-light',
        bgPrimary: 'bg-tale-business',
        textPrimary: 'text-tale-business',
        hoverPrimary: 'hover:bg-tale-business',
        ringPrimary: 'focus:ring-tale-business',
        borderPrimary: 'border-tale-business',
    };

    // Normal theme classes
    const normalClasses = {
        gradientPrimary: 'bg-tale-normal',
        gradientLight: 'bg-tale-normal-light',
        bgPrimary: 'bg-tale-normal',
        textPrimary: 'text-tale-normal',
        hoverPrimary: 'hover:bg-tale-normal',
        ringPrimary: 'focus:ring-tale-normal',
        borderPrimary: 'border-tale-normal',
    };

    // Use the appropriate classes based on isCompany
    const classes = isCompany ? businessClasses : normalClasses;

    // Scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Focus input when chat opens
    useEffect(() => {
        if (isOpen && hasOpenedOnce && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen, hasOpenedOnce]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleOpen = async () => {
        setIsOpen(true);
        if (!hasOpenedOnce) {
            // Load conversation history on first open
            try {
                const history = await aiAssistantService.getHistory();
                const formattedMessages = history.reverse().flatMap(item => [
                    { type: 'user', content: item.userQuestion, timestamp: item.createdAt },
                    { type: 'assistant', content: item.assistantResponse, timestamp: item.createdAt }
                ]);
                setMessages(formattedMessages);
            } catch (error) {
                console.error('Error loading history:', error);
            }
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleWelcomeNext = () => {
        setHasOpenedOnce(true);
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isLoading) return;

        const userMessage = inputMessage.trim();
        
        // Add user message to UI immediately
        const userMessageObj = {
            type: 'user',
            content: userMessage,
            timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, userMessageObj]);
        setInputMessage('');
        setIsLoading(true);

        try {
            // Send to AI service
            const response = await aiAssistantService.askQuestion(userMessage);
            
            // Update conversation ID if it's a new conversation
            if (response.conversationId && conversationId !== response.conversationId) {
                setConversationId(response.conversationId);
            }

            // Add AI response to UI
            const aiMessageObj = {
                type: 'assistant',
                content: response.answer,
                timestamp: response.timestamp
            };
            
            setMessages(prev => [...prev, aiMessageObj]);
            
        } catch (error) {
            console.error('Error sending message:', error);
            
            // Add error message
            const errorMessageObj = {
                type: 'assistant',
                content: "Sorry, I encountered an error. Please try again.",
                timestamp: new Date().toISOString()
            };
            
            setMessages(prev => [...prev, errorMessageObj]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleResetContext = async () => {
        try {
            await aiAssistantService.resetContext();
            setMessages([]);
            setConversationId(null);
            // Optional: Show success message
            const resetMessage = {
                type: 'system',
                content: "Conversation context has been reset.",
                timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, resetMessage]);
        } catch (error) {
            console.error('Error resetting context:', error);
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <>
            {!isOpen && (
                <div onClick={handleOpen}
                    className={`fixed right-5 bottom-5 w-14 h-14 flex justify-center items-center px-1 py-1 rounded-full bg-gradient-to-r ${classes.gradientPrimary} cursor-pointer shadow-lg hover:scale-105 transition-transform duration-200 z-50`}>
                    <img src={IA} className="w-10" alt="AI Icon" loading="lazy"/>
                </div>
            )}
            
            {isOpen && (
                <>
                    {!hasOpenedOnce ? (
                        <div className={`fixed right-5 bottom-20 bg-gradient-to-br from-white to-gray-50 p-8 rounded-xl shadow-2xl max-w-sm w-80 flex flex-col items-center space-y-6 text-center z-50`}>
                            <div className={`bg-gradient-to-r ${classes.gradientPrimary} p-3 rounded-full`}>
                                <img src={IA} className="w-10" alt="AI Icon" loading="lazy"/>
                            </div>
                            <h2 className="text-3xl font-bold text-purple-950">Talebot</h2>
                            <p className="text-base text-gray-600 leading-relaxed">
                                I'm your virtual assistant here to help make your experience seamless and enjoyable.
                                Ask me about surveys, reports, or anything related to the platform!
                            </p>
                            <button
                                onClick={handleWelcomeNext}
                                className={`w-full py-3 px-6 text-white font-semibold rounded-xl bg-gradient-to-r ${classes.gradientPrimary} ${classes.hoverPrimary} focus:outline-none focus:ring-2 ${classes.ringPrimary} focus:ring-opacity-75 shadow-lg transform hover:scale-105 transition-transform duration-200`}>
                                Start Chatting
                            </button>
                        </div>
                    ) : (
                        <div
                            className="fixed right-5 bottom-20 w-full max-w-md mx-auto bg-white rounded-2xl shadow-2xl flex flex-col z-50 p-1"
                            style={{height: '70vh', maxHeight: '600px'}}>
                            
                            {/* Header */}
                            <div className={`flex items-center justify-between p-4 bg-gradient-to-r ${classes.gradientLight} border-b rounded-t-2xl`}>
                                <div className="flex items-center space-x-3">
                                    <div className={`bg-gradient-to-r ${classes.gradientPrimary} p-2 rounded-full`}>
                                        <img src={IA} className="w-10" alt="AI Icon" loading="lazy"/>
                                    </div>
                                    <div>
                                        <h2 className={`text-lg font-bold ${classes.textPrimary}`}>Talebot</h2>
                                        <div className="flex items-center space-x-1.5">
                                            <span className="h-2 w-2 bg-green-500 rounded-full"></span>
                                            <span className="text-xs text-gray-600">Online</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button 
                                        onClick={handleResetContext}
                                        className="text-xs bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 px-3 py-1.5 rounded-full transition-colors duration-200"
                                        title="Reset conversation context">
                                        Reset Context
                                    </button>
                                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                                             viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Messages Container */}
                            <div className="flex-1 p-4 space-y-4 overflow-y-auto bg-gradient-to-b from-blue-50/50 to-white">
                                {messages.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-center p-4">
                                        <div className={`bg-gradient-to-r ${classes.gradientPrimary} p-4 rounded-full mb-4`}>
                                            <img src={IA} className="w-12" alt="AI Icon"/>
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">Welcome to Talebot!</h3>
                                        <p className="text-gray-600 mb-4">
                                            I'm here to help you with survey platform queries and reports.
                                            Ask me anything!
                                        </p>
                                        <div className="text-sm text-gray-500">
                                            <p>Try asking:</p>
                                            <p className="mt-2">• "Show me my latest survey results"</p>
                                            <p>• "Generate a report for Q3"</p>
                                            <p>• "Help me create a new survey"</p>
                                        </div>
                                    </div>
                                ) : (
                                    messages.map((message, index) => (
                                        <div key={index} className={`flex ${message.type === 'user' ?'flex-row-reverse':'flex-row'}`}>
                                            {message.type === 'assistant' && (
                                                <div className="flex-shrink-0 mr-2 mt-1">
                                                    <div className={`w-8 h-8 bg-gradient-to-r ${classes.gradientPrimary} rounded-full flex items-center justify-center`}>
                                                        <span className="text-white text-xs font-bold">TB</span>
                                                    </div>
                                                </div>
                                            )}
                                            <div className={`max-w-xs ${message.type === 'user' ? 'order-1' : 'order-2'}`}>
                                                <div className={`p-3 rounded-2xl shadow ${message.type === 'user' 
                                                    ? `bg-gradient-to-br ${classes.gradientPrimary} text-white rounded-br-none` 
                                                    : 'bg-gray-100 text-gray-800 rounded-bl-none'}`}>
                                                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                                                    <div className={`text-xs mt-1 ${message.type === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                                                        {formatTime(message.timestamp)}
                                                    </div>
                                                </div>
                                            </div>
                                            {message.type === 'user' && (
                                                <div className="flex-shrink-0 ml-2 mt-1">
                                                    <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                                                        <span className="text-white text-xs font-bold">ME</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                                {isLoading && (
                                    <div className="flex justify-start">
                                        <div className="flex-shrink-0 mr-2 mt-1">
                                            <div className={`w-8 h-8 bg-gradient-to-r ${classes.gradientPrimary} rounded-full flex items-center justify-center`}>
                                                <span className="text-white text-xs font-bold">TB</span>
                                            </div>
                                        </div>
                                        <div className="bg-gray-100 p-3 rounded-2xl rounded-bl-none shadow">
                                            <div className="flex space-x-1">
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-75"></div>
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-150"></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Input Area */}
                            <div className="p-4 bg-gray-50 border-t rounded-b-2xl">
                                <div className="flex items-center space-x-3">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        placeholder="Ask about surveys, reports, or platform features..."
                                        value={inputMessage}
                                        onChange={(e) => setInputMessage(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        disabled={isLoading}
                                        className={`flex-1 bg-white border border-gray-300 rounded-full py-3 px-5 text-sm text-gray-800 placeholder-gray-500 outline-none focus:ring-2 ${classes.ringPrimary} focus:border-transparent disabled:opacity-50`}
                                    />
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={isLoading || !inputMessage.trim()}
                                        className={`p-3 rounded-full transition-colors duration-200 ${isLoading || !inputMessage.trim()
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : `bg-gradient-to-r ${classes.gradientPrimary} text-white ${classes.hoverPrimary}`
                                        }`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transform rotate-45"
                                             fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default AI;